import * as React from 'react';

import { TextField, Select } from '@rematter/ui-web';
import { Clock } from 'lucide-react';

import { NodeConfigProps } from '../../types';

import { ScheduleTriggerConfigType, CRON_PRESETS } from './schema';

/**
 * Configuration panel for Schedule triggers.
 * Provides cron expression builder and timezone selection.
 */
export const ScheduleTriggerConfig: React.FC<
  NodeConfigProps<ScheduleTriggerConfigType>
> = ({ config, onUpdate }) => {
  const [cronExpression, setCronExpression] = React.useState<string>(
    config.cronExpression || '',
  );
  const [timezone, setTimezone] = React.useState<string>(
    config.timezone || 'UTC',
  );
  const [cronPreset, setCronPreset] = React.useState<string>('custom');

  const handleUpdate = React.useCallback(
    (updates: Partial<ScheduleTriggerConfigType>) => {
      onUpdate({ ...config, ...updates });
    },
    [config, onUpdate],
  );

  const handleCronExpressionChange = React.useCallback(
    (value: string) => {
      setCronExpression(value);
      handleUpdate({ cronExpression: value });
    },
    [handleUpdate],
  );

  const handleTimezoneChange = React.useCallback(
    (value: string) => {
      setTimezone(value);
      handleUpdate({ timezone: value });
    },
    [handleUpdate],
  );

  const handlePresetChange = React.useCallback(
    (value: string) => {
      setCronPreset(value);
      const preset = CRON_PRESETS.find((p) => p.id === value);
      if (preset && preset.cron) {
        handleCronExpressionChange(preset.cron);
      }
    },
    [handleCronExpressionChange],
  );

  // Parse cron expression to show human-readable description
  const cronDescription = React.useMemo(() => {
    if (!cronExpression) return '';
    const preset = CRON_PRESETS.find((p) => p.cron === cronExpression);
    return preset ? preset.name : 'Custom schedule';
  }, [cronExpression]);

  return (
    <div className="@ScheduleTriggerConfig space-y-4">
      <div>
        <label className="mb-1 block text-sm font-medium">
          Schedule Preset
        </label>
        <Select
          placeholder="Select a preset..."
          value={cronPreset}
          onChange={handlePresetChange}
          options={CRON_PRESETS.map((p) => ({ key: p.id, label: p.name }))}
        />
        <p className="mt-1 text-xs text-neutral/60">
          Choose a common schedule or select Custom to define your own
        </p>
      </div>

      <div>
        <TextField
          label="Cron Expression"
          value={cronExpression}
          onChange={handleCronExpressionChange}
          placeholder="* * * * *"
          className="font-mono text-sm"
          description="Five fields: minute hour day month weekday"
        />
        {cronDescription && (
          <div className="mt-2 flex items-center gap-2 text-sm text-neutral/70">
            <Clock className="size-4" />
            <span>{cronDescription}</span>
          </div>
        )}
      </div>

      <div>
        <TextField
          label="Timezone"
          value={timezone}
          onChange={handleTimezoneChange}
          placeholder="UTC"
          description="Timezone for the schedule (e.g., UTC, America/New_York)"
        />
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Cron Format:</p>
        <div className="space-y-1 font-mono text-xs text-neutral/70">
          <div>
            <code>* * * * *</code> = minute hour day month weekday
          </div>
          <div>
            <code>*/5</code> = every 5 units
          </div>
          <div>
            <code>1-5</code> = range from 1 to 5
          </div>
          <div>
            <code>1,3,5</code> = specific values
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Use online cron expression generators if you
          need help building complex schedules.
        </p>
      </div>
    </div>
  );
};
